import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Button } from "../ui";
import { Modal, CloseButton } from "react-bootstrap";
import { MDBInput, MDBSpinner } from "mdb-react-ui-kit";
import { api } from "../../services";
import { toast } from "react-toastify";
import { ChevronRight, ChevronLeft, Eye, EyeOff } from "lucide-react";
import { normalizeRestaurantId } from "../../utils/utils";
import { parseRolesListResponse, formatRoleDisplayName, formatPermissionLabel } from "../../utils/rbacApi";
import { translateRbacApiMessage } from "../../utils/translateRbacApiMessage";
import {
  reconcileUsersMutateRequiresRolesView,
  ROLES_VIEW_PERM,
  permissionDomainIdContainingKey,
} from "../../utils/reconcileUsersMutateRoleDeps";
import { normalizeAppManagementPermissions } from "../../utils/normalizeAppManagementPermissions";
import { usePermission } from "../../hooks/usePermission";
import { useTranslation } from "react-i18next";
import PermissionAccordion from "../rbac/PermissionAccordion";
import { ONEHUB_BRAND_FORM_CONTROLS_CSS, ONEHUB_BRAND_HEX } from "../rbac/brandFormControlsCss";
import { isValidEmail, isValidKsaPhone, normalizeKsaMobile, formatKsaE164 } from "../../utils/validators";
import { MODAL_PAD_X, MODAL_PAD_Y } from "./userManagementModalLayout";

const BRAND = ONEHUB_BRAND_HEX;
/** Figma solid primary for bars / borders where CSS var is awkward. */
const WIZARD_PRIMARY_HEX = "#083B4C";
/** Review & invite (Figma step 5) — user details card. */
const WIZARD_MODAL_TITLE_COLOR = "#303030";
const WIZARD_PROGRESS_BORDER = "#D1D1D1";
const REVIEW_USER_CARD_RADIUS = 2;
const REVIEW_USER_CARD_PAD = 14;
const REVIEW_USER_CARD_GAP = 14;
const REVIEW_FIGMA_TEXT = "#030F1B";
const REVIEW_FIGMA_ROW_BORDER = "#E8E8E8";
const REVIEW_AVATAR_BG_FIGMA = "#FFF4EC";
const REVIEW_AVATAR_FG_FIGMA = "#FF9B53";
const REVIEW_BRANCH_PILL_BG_FIGMA = "#FFFAEE";
const REVIEW_BRANCH_PILL_BORDER_FIGMA = "#FFE5AA";
const REVIEW_BRANCH_PILL_TEXT_FIGMA = "#030F1B";
const REVIEW_CARD_BORDER = "#D1D1D1";
const REVIEW_SUBTITLE_COLOR = "#474747";
const FONT_UI = "Inter, sans-serif";

/** Role & access step: neutrals + brand accents (see ONEHUB_BRAND_HEX). */
const ROLE_CARD_BORDER = "#D1D1D1";
const ROLE_CARD_RADIUS = 2;
const ROLE_DESC_COLOR = "#030F1B";
const ROLE_STEP_HINT = "#474747";
const ROLE_CHIP_BG = "#FBF9F5";
const ROLE_CHIP_BORDER = "#F8F3EA";
const ROLE_CHIP_TEXT = "#030F1B";

/** Figma — role step typography (fixed px + 150% line-height). */
const ROLE_SECTION_TITLE_FS = 14; // "SELECT A ROLE"
const ROLE_SECTION_TITLE_LH = 1.5;
const ROLE_SECTION_TITLE_WEIGHT = 600;
const ROLE_SECTION_TITLE_COLOR = "#303030";
const ROLE_CARD_NAME_FS = 12; // role title in selection cards (increased for readability)
const ROLE_CARD_NAME_LH = 1.5;
const ROLE_CARD_NAME_WEIGHT = 700;
const ROLE_CARD_NAME_COLOR = "#030F1B"; // Text/Light/Text
const ROLE_CARD_DESC_FS = 10;
const ROLE_CARD_DESC_LH = 1.5;
const ROLE_CARD_DESC_WEIGHT = 400;
const NEW_ROLE_HEADING_FS = 12; // "ADD NEW ROLE" (increased for readability)
const NEW_ROLE_HEADING_LH = 1.5;
const NEW_ROLE_HEADING_WEIGHT = 700;

/** Add-new-role / Permissions block (Figma — step 2). */
const NEW_ROLE_BORDER = "#D1D1D1";
const NEW_ROLE_LABEL = "#303030";
const NEW_ROLE_INNER_BG = "#FFFFFF";
const NEW_ROLE_INPUT_BORDER = "#BABABA";
const NEW_ROLE_SWITCH_ON = "#083B4C";
const NEW_ROLE_SWITCH_OFF = "#D9D9D9";
const NEW_ROLE_SWITCH_W = 30;
const NEW_ROLE_SWITCH_H = 17;
const NEW_ROLE_SWITCH_THUMB = 13;
const NEW_ROLE_SWITCH_PAD = 2;

/**
 * Permission accordion in the “create custom role” panel — same layout tokens as
 * `RoleWizardModal` (toolbar, section shells, detail rows, summary strip).
 */
const ADD_USER_PERM_UI_BORDER = "#e0e0e0";
const ADD_USER_PERM_UI_TITLE = "#111827";
const ADD_USER_PERM_UI_LABEL = "#374151";

const ADD_USER_WIZARD_PERMISSION_ACCORDION_STYLES = `
.add-user-wizard-permissions-block {
  margin-top: 0.2rem;
}
.add-user-wizard-permissions-block .permission-accordion-figma .permission-accordion-toolbar {
  margin-bottom: 0.45rem !important;
}
.add-user-wizard-permissions-block .permission-accordion-figma .permission-accordion-toolbar span.fw-bold.small {
  color: #000000 !important;
  font-size: 11px !important;
  letter-spacing: 0.08em !important;
}
.add-user-wizard-permissions-block .permission-accordion-figma .permission-accordion-section-expand,
.add-user-wizard-permissions-block .permission-accordion-figma .permission-accordion-section-expand.btn {
  text-transform: none !important;
}
.add-user-wizard-permissions-block .permission-accordion-figma .permission-accordion-section-expand .fw-semibold {
  color: ${ADD_USER_PERM_UI_TITLE} !important;
  line-height: 1.3 !important;
  text-transform: none !important;
}
.add-user-wizard-permissions-block .permission-accordion-figma .permission-accordion-section-expand svg {
  width: 17px !important;
  height: 17px !important;
}
.add-user-wizard-permissions-block .permission-accordion-figma .permission-accordion-toolbar label[for="perm-select-all"] {
  color: ${ADD_USER_PERM_UI_LABEL} !important;
}
.add-user-wizard-permissions-block .permission-accordion-figma .permission-accordion-section-shell {
  border-color: ${ADD_USER_PERM_UI_BORDER} !important;
  border-radius: 4px !important;
  --perm-control-col: 2.25rem;
}
.add-user-wizard-permissions-block .permission-accordion-figma .permission-accordion-section-head {
  min-height: 40px !important;
  column-gap: 0.75rem !important;
  padding-top: 0.3125rem !important;
  padding-bottom: 0.3125rem !important;
  padding-left: 0.625rem !important;
  padding-right: 0.625rem !important;
}
.add-user-wizard-permissions-block .permission-accordion-figma > .d-flex.flex-column.gap-2 {
  gap: 0.3125rem !important;
}
.add-user-wizard-permissions-block .permission-accordion-figma .permission-accordion-details {
  padding-block: 0.45rem 0.55rem !important;
  padding-inline: 0.6875rem !important;
}
.add-user-wizard-permissions-block .permission-accordion-figma .permission-accordion-details-rows {
  gap: 0.3125rem !important;
}
.add-user-wizard-permissions-block .permission-accordion-figma .perm-detail-row {
  column-gap: 0.75rem !important;
}
.add-user-wizard-permissions-block .permission-accordion-figma .permission-accordion-summary {
  margin-inline: 0 !important;
  margin-top: 0.45rem !important;
  padding-block: 0.45rem !important;
  padding-inline: 0.6875rem !important;
  font-size: 0.8125rem !important;
  line-height: 1.35 !important;
}
.add-user-wizard-permissions-block .permission-accordion-figma .permission-accordion-summary-dot {
  width: 7px !important;
  height: 7px !important;
}
.add-user-wizard-permissions-block .permission-accordion-figma .perm-detail-label-col {
  font-size: 0.8125rem !important;
  line-height: 1.32 !important;
}
`;

/** Brand & branch step — cream “select all” bar, neutral cards, aligned branch rows. */
const BRANCH_SELECT_ALL_BG = "#FBF9F5";
const BRANCH_SELECT_ALL_BORDER = "#EDE6DC";
const BRANCH_SUMMARY_TEXT = "#474747";
const BRANCH_CARD_BORDER = "#D1D1D1";
const BRANCH_CARD_RADIUS = 8;
const BRANCH_EXPANDED_BG = "#ECEFF1";
const BRANCH_EXPANDED_BORDER_TOP = "#D1D1D1";
const BRANCH_SECTION_TITLE_COLOR = "#303030";
/** Align branch row with brand title: store checkbox column (16) + gap-2 (8) after same px-3 as expanded panel. */
const BRANCH_CHECKBOX_ALIGN_INSET = 22;

/** Custom new-role switch thumb `insetInlineStart` (logical; correct in LTR and RTL). */
function newRoleSwitchThumbInset(creatingRole) {
  return creatingRole
    ? NEW_ROLE_SWITCH_W - NEW_ROLE_SWITCH_THUMB - NEW_ROLE_SWITCH_PAD
    : NEW_ROLE_SWITCH_PAD;
}

/** Aligned with restaurantStaffController.createStaffUser (normalized username). */
const USERNAME_MIN_LEN = 3;
const USERNAME_MAX_LEN = 64;
const USERNAME_PATTERN = /^[a-z0-9._-]+$/;

function normalizeUsernameForAccount(s) {
  return String(s ?? "").trim().toLowerCase();
}

/** Platform Admin role must not be assignable to staff (matches backend read-only Admin role). */
function isRoleExcludedForSubUser(role) {
  if (!role || role.name == null) return false;
  return String(role.name).trim().toLowerCase() === "admin";
}

function randomPassword(length = 14) {
  const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@%*";
  const arr = new Uint8Array(length);
  crypto.getRandomValues(arr);
  let s = "";
  for (let i = 0; i < length; i++) s += chars[arr[i] % chars.length];
  return s;
}

/** Stepper: top segment lines (Figma — 3px active bar, #083B4C). */
function StepBar({ current, labels, className = "mb-3" }) {
  const allBarsFilled = current === labels.length - 1;
  return (
    <div className={className}>
      <div className="d-flex w-100" style={{ gap: 12 }} role="tablist" aria-label="Wizard steps">
        {labels.map((label, i) => {
          const active = i === current;
          const completed = i < current;
          const barBg = allBarsFilled ? WIZARD_PRIMARY_HEX : active ? WIZARD_PRIMARY_HEX : completed ? "#BABABA" : "#e8ecf1";
          const labelColor = allBarsFilled ? (active ? WIZARD_PRIMARY_HEX : "#474747") : active ? WIZARD_PRIMARY_HEX : completed ? "#474747" : "#BABABA";
          const labelWeight = active ? 700 : completed ? 600 : 500;
          return (
            <div key={label} className="flex-fill text-center" style={{ minWidth: 0, paddingInline: 2 }}>
              <div
                style={{
                  height: 3,
                  borderRadius: 2,
                  background: barBg,
                  transition: "background 0.2s ease",
                }}
              />
              <div
                style={{
                  fontSize: 12,
                  fontWeight: labelWeight,
                  color: labelColor,
                  paddingTop: 10,
                  paddingBottom: 2,
                  lineHeight: 1.5,
                  fontFamily: FONT_UI,
                }}
              >
                {label}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Figma review rows — 12px label 400 / value 600, #030F1B, hairline dividers. */
function ReviewFigmaRow({ label, value, monospace, dividerBelow = true }) {
  return (
    <div
      className="d-flex justify-content-between align-items-center"
      style={{
        padding: "4px 0",
        borderBottom: dividerBelow ? `1px solid ${REVIEW_FIGMA_ROW_BORDER}` : "none",
        fontFamily: FONT_UI,
      }}
    >
      <span style={{ fontSize: 12, fontWeight: 400, color: REVIEW_FIGMA_TEXT, flexShrink: 0, lineHeight: 1.5 }}>
        {label}
      </span>
      <span
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: REVIEW_FIGMA_TEXT,
          textAlign: "end",
          wordBreak: "break-word",
          lineHeight: 1.5,
          minWidth: 0,
          marginInlineStart: 12,
          ...(monospace ? { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" } : {}),
        }}
      >
        {value}
      </span>
    </div>
  );
}

export default function AddUserWizard({ show, onHide, restaurantId, onCreated }) {
  const { t, i18n } = useTranslation("translation");
  const isRtl = i18n.dir() === "rtl";
  const { bypass, can } = usePermission();
  /** "Create custom role" in this wizard matches Roles module only (`POST /v1/roles` uses roles:create). `users:create` invites staff but must not enable the new-role builder. */
  const canCreateRole = can("roles", "create");

  const stepLabels = useMemo(
    () => [
      t("addUserWizard.stepProfile"),
      t("addUserWizard.stepRoleAccess"),
      t("addUserWizard.stepSellerAccounts"),
      t("addUserWizard.stepReview"),
    ],
    [t]
  );

  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const [generatePassword, setGeneratePassword] = useState(false);
  const [profileErrors, setProfileErrors] = useState({});
  const [roleErrors, setRoleErrors] = useState({});
  const [scopeErrors, setScopeErrors] = useState({});
  const [checkingIdentity, setCheckingIdentity] = useState(false);

  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [creatingRole, setCreatingRole] = useState(true);
  const [newRoleName, setNewRoleName] = useState("");
  const [newRoleDesc, setNewRoleDesc] = useState("");
  const [permRegistry, setPermRegistry] = useState({});
  const [permDomains, setPermDomains] = useState([]);
  const [selectedPerms, setSelectedPerms] = useState([]);
  const [expandedPermMods, setExpandedPermMods] = useState(() => new Set());

  const [stores, setStores] = useState([]);
  const [loadingStores, setLoadingStores] = useState(false);
  const [fullBranchAccess, setFullBranchAccess] = useState(false);
  const [selectedBranchIds, setSelectedBranchIds] = useState([]);
  const [expandedStores, setExpandedStores] = useState(() => new Set());
  /** Role cards on step 1: expand to list all permissions for that role. */
  const [expandedRoleDetailIds, setExpandedRoleDetailIds] = useState(() => new Set());

  const reset = useCallback(() => {
    setStep(0);
    setSubmitting(false);
    setFirstName("");
    setLastName("");
    setUsername("");
    setEmail("");
    setPhone("");
    setPassword("");
    setConfirmPassword("");
    setShowPass(false);
    setShowConfirmPass(false);
    setGeneratePassword(false);
    setProfileErrors({});
    setRoleErrors({});
    setScopeErrors({});
    setCheckingIdentity(false);
    setSelectedRoleId("");
    setCreatingRole(canCreateRole);
    setNewRoleName("");
    setNewRoleDesc("");
    setSelectedPerms([]);
    setExpandedPermMods(new Set());
    setPermDomains([]);
    setStores([]);
    setFullBranchAccess(false);
    setSelectedBranchIds([]);
    setExpandedStores(new Set());
    setExpandedRoleDetailIds(new Set());
  }, [canCreateRole]);

  function toggleRolePermissionsExpanded(roleId) {
    const id = String(roleId);
    setExpandedRoleDetailIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  useEffect(() => {
    if (!show) reset();
  }, [show, reset]);

  /** Without `roles:create`, never stay in new-role mode (do not skip while access-summary is loading — default state is creatingRole true). */
  useEffect(() => {
    if (!canCreateRole) setCreatingRole(false);
  }, [canCreateRole]);

  useEffect(() => {
    if (step !== 1) return;
    setLoadingRoles(true);
    Promise.all([
      api.get("/v1/roles"),
      api.get("/v1/permissions-registry"),
    ])
      .then(([rolesRes, permRes]) => {
        setRoles(parseRolesListResponse(rolesRes));
        const pdata = permRes.data || {};
        setPermRegistry(pdata.appRouteRegistry || pdata.registry || {});
        setPermDomains(Array.isArray(pdata.domains) ? pdata.domains : []);
      })
      .catch(() => toast.error(t("rbac.failedLoadRoles")))
      .finally(() => setLoadingRoles(false));
  }, [step, t]);

  const assignableRoles = useMemo(() => roles.filter((r) => !isRoleExcludedForSubUser(r)), [roles]);

  useEffect(() => {
    if (!selectedRoleId || !roles.length) return;
    const r = roles.find((x) => String(x._id) === String(selectedRoleId));
    if (r && isRoleExcludedForSubUser(r)) {
      setSelectedRoleId("");
      setCreatingRole(canCreateRole);
    }
  }, [roles, selectedRoleId, canCreateRole]);

  useEffect(() => {
    if (step !== 2) return;
    setLoadingStores(true);
    api
      .get("/stores/my")
      .then((res) => setStores(res.data.stores || []))
      .catch(() => toast.error(t("rbac.failedLoadStores")))
      .finally(() => setLoadingStores(false));
  }, [step, t]);

  useEffect(() => {
    if (generatePassword) {
      const p = randomPassword();
      setPassword(p);
      setConfirmPassword(p);
    }
  }, [generatePassword]);

  function togglePermExpand(mod) {
    setExpandedPermMods((prev) => {
      const next = new Set(prev);
      if (next.has(mod)) next.delete(mod);
      else next.add(mod);
      return next;
    });
  }

  function normalizedPhoneForDuplicateCheck(phoneVal) {
    const phoneTrimmed = String(phoneVal ?? "").trim();
    if (!phoneTrimmed || !isValidKsaPhone(phoneTrimmed)) return "";
    try {
      return formatKsaE164(normalizeKsaMobile(phoneTrimmed));
    } catch {
      return "";
    }
  }

  function computeStoresForAssignment() {
    const rid = normalizeRestaurantId(restaurantId);
    const storesForAssignment = [];
    if (bypass && stores.length > 0) {
      if (fullBranchAccess) {
        storesForAssignment.push(...stores.map((store) => store._id));
      } else if (selectedBranchIds.length > 0) {
        const uniqueStoreIds = new Set();
        stores.forEach((store) => {
          if (store.branches?.some((branch) => selectedBranchIds.includes(String(branch._id)))) {
            uniqueStoreIds.add(store._id);
          }
        });
        storesForAssignment.push(...Array.from(uniqueStoreIds));
      } else {
        storesForAssignment.push(...stores.map((store) => store._id));
      }
    } else if (rid) {
      storesForAssignment.push(rid);
    }
    return storesForAssignment;
  }

  function computeProfileErrorsSync(overrides = {}) {
    const fn = (overrides.firstName ?? firstName).trim();
    const ln = (overrides.lastName ?? lastName).trim();
    const u =
      overrides.username !== undefined
        ? normalizeUsernameForAccount(String(overrides.username))
        : normalizeUsernameForAccount(username);
    const em = (overrides.email !== undefined ? String(overrides.email) : email).trim();
    const ph = overrides.phone !== undefined ? String(overrides.phone) : phone;
    const pw = overrides.password !== undefined ? overrides.password : password;
    const cpw = overrides.confirmPassword !== undefined ? overrides.confirmPassword : confirmPassword;
    const gen = overrides.generatePassword !== undefined ? overrides.generatePassword : generatePassword;

    const errs = {};
    if (!fn) errs.firstName = t("addUserWizard.firstNameRequired");
    if (!ln) errs.lastName = t("addUserWizard.lastNameRequired");
    if (!u) errs.username = t("addUserWizard.usernameRequired");
    else if (u.length < USERNAME_MIN_LEN || u.length > USERNAME_MAX_LEN) {
      errs.username = t("addUserWizard.usernameLength", {
        min: USERNAME_MIN_LEN,
        max: USERNAME_MAX_LEN,
      });
    } else if (!USERNAME_PATTERN.test(u)) {
      errs.username = t("addUserWizard.invalidUsernameChars");
    }
    if (em && !isValidEmail(em)) errs.email = t("addUserWizard.invalidEmail");
    if (ph.trim() && !isValidKsaPhone(ph)) errs.phone = t("addUserWizard.invalidKsaPhone");
    if (!gen) {
      const pwTrim = String(pw ?? "").trim();
      const cpwTrim = String(cpw ?? "").trim();
      if (!pwTrim) errs.password = t("addUserWizard.passwordRequired");
      else if (pwTrim.length < 8) errs.password = t("Min 8 characters");
      if (!cpwTrim) errs.confirmPassword = t("addUserWizard.confirmPasswordRequired");
      else if (cpwTrim.length < 8) errs.confirmPassword = t("Min 8 characters");
      else if (pwTrim && cpwTrim !== pwTrim) errs.confirmPassword = t("Passwords do not match");
    } else {
      const pwStr = String(pw ?? "");
      if (!pwStr) errs.password = t("addUserWizard.passwordRequired");
      else if (pwStr.length < 8) errs.password = t("Min 8 characters");
    }
    return errs;
  }

  /** Merge sync validation into profileErrors for only the given keys (other keys unchanged). */
  function mergeProfileFieldErrors(fieldKeys, overrides = {}) {
    const sync = computeProfileErrorsSync(overrides);
    setProfileErrors((prev) => {
      const next = { ...prev };
      for (const key of fieldKeys) {
        if (sync[key]) next[key] = sync[key];
        else delete next[key];
      }
      return next;
    });
  }

  /** Password / confirm: validate both fields on blur when entering passwords manually. */
  function validatePasswordFieldsOnBlur() {
    if (generatePassword) return;
    mergeProfileFieldErrors(["password", "confirmPassword"]);
  }

  async function runProfileAvailabilityCheckAndSetErrors(overrides = {}, options = {}) {
    const mergeKeys = options.mergeKeys;
    try {
      const u =
        overrides.username !== undefined
          ? normalizeUsernameForAccount(String(overrides.username))
          : normalizeUsernameForAccount(username);
      const emailTrim = (overrides.email !== undefined ? String(overrides.email) : email).trim();
      const phonePayload = normalizedPhoneForDuplicateCheck(
        overrides.phone !== undefined ? String(overrides.phone) : phone
      );
      const params = { username: u };
      if (emailTrim && isValidEmail(emailTrim)) params.email = emailTrim.toLowerCase();
      if (phonePayload) params.phone = phonePayload;
      const { data } = await api.get("/v1/staff-users/check-identity", { params });
      const mergedAvail = {};
      if (data.usernameTaken) mergedAvail.username = t("addUserWizard.usernameTaken");
      if (emailTrim && isValidEmail(emailTrim) && data.emailTaken) mergedAvail.email = t("addUserWizard.emailTaken");
      if (phonePayload && data.phoneTaken) mergedAvail.phone = t("addUserWizard.phoneTaken");

      const sync = computeProfileErrorsSync(overrides);

      if (mergeKeys == null) {
        setProfileErrors({ ...sync, ...mergedAvail });
        return (
          Object.keys(sync).length === 0 &&
          Object.keys(mergedAvail).length === 0
        );
      }

      setProfileErrors((prev) => {
        const next = { ...prev };
        for (const k of mergeKeys) {
          if (sync[k]) next[k] = sync[k];
          else if (mergedAvail[k]) next[k] = mergedAvail[k];
          else delete next[k];
        }
        return next;
      });

      return !mergeKeys.some((k) => !!(sync[k] || mergedAvail[k]));
    } catch {
      toast.error(t("addUserWizard.identityCheckFailed"));
      return false;
    }
  }

  /** Identity fields: validate only blurred field(s), then availability when sync passes for those fields. */
  async function validateProfileIdentityOnBlur(overrides = {}) {
    const identityFields = ["username", "email", "phone"];
    const mergeKeys = identityFields.filter((k) => Object.prototype.hasOwnProperty.call(overrides, k));
    if (mergeKeys.length === 0) return;

    const sync = computeProfileErrorsSync(overrides);
    setProfileErrors((prev) => {
      const next = { ...prev };
      for (const key of mergeKeys) {
        if (sync[key]) next[key] = sync[key];
        else delete next[key];
      }
      return next;
    });
    if (mergeKeys.some((k) => !!sync[k])) return;

    await runProfileAvailabilityCheckAndSetErrors(overrides, { mergeKeys });
  }

  function validateRole() {
    const errs = {};
    const isNewRoleFlow = creatingRole && canCreateRole;
    const selected = roles.find((r) => String(r._id) === String(selectedRoleId));
    if (selected && isRoleExcludedForSubUser(selected)) {
      errs.general = t("addUserWizard.adminRoleNotAllowedSubUser");
    }
    if (!selectedRoleId && !isNewRoleFlow) {
      errs.general = errs.general || t("rbac.selectOrCreateRole");
    }
    if (isNewRoleFlow && !newRoleName.trim()) {
      errs.newRoleName = t("rbac.roleNameRequired");
    }
    const nr = newRoleName.trim();
    if (isNewRoleFlow && newRoleName.trim() && (nr.length < 3 || nr.length > 25)) {
      errs.newRoleName =
        nr.length < 3 ? t("rbac.roleNameMinLength") : t("rbac.roleNameMaxLength");
    }
    if (isNewRoleFlow && (!Array.isArray(selectedPerms) || selectedPerms.length === 0)) {
      errs.permissions = t("rbac.permissionRequiredForCreate");
    }
    setRoleErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function validateNewRoleNameOnBlur() {
    if (!creatingRole || !canCreateRole) return;
    if (!newRoleName.trim()) {
      setRoleErrors((prev) => ({ ...prev, newRoleName: t("rbac.roleNameRequired") }));
      return;
    }
    const nr = newRoleName.trim();
    if (nr.length < 3 || nr.length > 25) {
      setRoleErrors((prev) => ({
        ...prev,
        newRoleName: nr.length < 3 ? t("rbac.roleNameMinLength") : t("rbac.roleNameMaxLength"),
      }));
      return;
    }
    setRoleErrors((prev) => ({ ...prev, newRoleName: undefined }));
  }

  function validateScope() {
    const errs = {};
    if (bypass && !loadingStores && stores.length === 0) {
      errs.general = t("addUserWizard.noSellerAccountsAvailable");
    }
    setScopeErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function goForward() {
    if (step === 0) {
      const syncErrs = computeProfileErrorsSync();
      setProfileErrors(syncErrs);
      if (Object.keys(syncErrs).length > 0) return;
      setCheckingIdentity(true);
      try {
        const ok = await runProfileAvailabilityCheckAndSetErrors();
        if (!ok) return;
      } finally {
        setCheckingIdentity(false);
      }
    } else if (step === 1) {
      if (!validateRole()) return;
    } else if (step === 2) {
      if (!validateScope()) return;
    }
    setStep((s) => s + 1);
  }
  const back = () => setStep((s) => s - 1);

  async function handleSubmit() {
    const rid = normalizeRestaurantId(restaurantId);
    if (!rid && !bypass) {
      toast.error(t("rbac.restaurantContextMissingDot"));
      return;
    }
    setScopeErrors({});
    setRoleErrors({});
    const syncErrs = computeProfileErrorsSync();
    setProfileErrors(syncErrs);
    if (Object.keys(syncErrs).length > 0) {
      setStep(0);
      return;
    }
    setCheckingIdentity(true);
    try {
      const availOk = await runProfileAvailabilityCheckAndSetErrors();
      if (!availOk) {
        setStep(0);
        return;
      }
    } finally {
      setCheckingIdentity(false);
    }
    if (!validateRole()) {
      setStep(1);
      return;
    }
    if (!validateScope()) {
      setStep(2);
      return;
    }
    setSubmitting(true);
    try {
      let rolePayload;
      if (creatingRole && canCreateRole) {
        rolePayload = {
          name: newRoleName.trim(),
          description: newRoleDesc.trim(),
          permissions: normalizeAppManagementPermissions(selectedPerms),
          ...(bypass ? { accountLevel: true } : { restaurantId: rid }),
        };
      } else {
        rolePayload = { roleId: selectedRoleId };
      }

      const storesForAssignment = computeStoresForAssignment();

      if (storesForAssignment.length === 0) {
        setScopeErrors({ general: t("rbac.restaurantContextRequiredForAssignmentsLong") });
        setStep(2);
        return;
      }

      const assignmentsPayload = storesForAssignment.map((storeId) => {
        let storeScope;
        if (fullBranchAccess) {
          storeScope = { scopeType: "restaurant", scopeIds: [] };
        } else {
          const store = stores.find((s) => String(s._id) === String(storeId));
          const storeBranchIds =
            store?.branches
              ?.filter((branch) => selectedBranchIds.includes(String(branch._id)))
              .map((branch) => branch._id) || [];

          if (
            storeBranchIds.length === (store?.branches?.length || 0) &&
            storeBranchIds.length > 0
          ) {
            storeScope = { scopeType: "store", scopeIds: [storeId] };
          } else {
            storeScope = { scopeType: "branch", scopeIds: storeBranchIds };
          }
        }
        return {
          restaurantId: storeId,
          scopeType: storeScope.scopeType,
          scopeIds: storeScope.scopeIds,
        };
      });

      const phoneTrimmed = phone.trim();
      const phonePayload = phoneTrimmed ? formatKsaE164(normalizeKsaMobile(phoneTrimmed)) : "";

      const staffPayload = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phonePayload,
        username: normalizeUsernameForAccount(username),
        password,
        role: rolePayload,
        assignments: assignmentsPayload,
      };

      const emailTrim = email.trim();
      if (emailTrim) staffPayload.email = emailTrim.toLowerCase();

      if (!bypass && rid) {
        staffPayload.restaurantId = rid;
      }

      await api.post("/v1/staff-users", staffPayload);

      toast.success(t("rbac.userCreatedSuccess"));
      onCreated?.();
      onHide();
    } catch (err) {
      toast.error(
        translateRbacApiMessage(err.response?.data?.message, t) || t("rbac.failedToCreateUser")
      );
    } finally {
      setSubmitting(false);
    }
  }

  function toggleBranch(branchId) {
    const id = String(branchId);
    setScopeErrors({});
    setSelectedBranchIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  function toggleAllBranchesInStore(store) {
    if (fullBranchAccess) return;
    setScopeErrors({});
    const bids = (store.branches || []).map((b) => String(b._id));
    const allOn = bids.length > 0 && bids.every((id) => selectedBranchIds.includes(id));
    setSelectedBranchIds((prev) => {
      if (allOn) return prev.filter((id) => !bids.includes(id));
      const set = new Set(prev);
      bids.forEach((id) => set.add(id));
      return [...set];
    });
  }

  function toggleStoreExpandedRow(storeId) {
    setExpandedStores((prev) => {
      const next = new Set(prev);
      if (next.has(storeId)) next.delete(storeId);
      else next.add(storeId);
      return next;
    });
  }

  const displayName = `${firstName} ${lastName}`.trim() || username || "—";
  const roleName =
    creatingRole && canCreateRole
      ? newRoleName || `(${t("rbac.newRoleParen")})`
      : formatRoleDisplayName(roles.find((r) => r._id === selectedRoleId)?.name) || "—";

  const branchStepSummary = useMemo(() => {
    const brandCount = stores.length;
    const totalBranches = stores.reduce((acc, s) => acc + (s.branches?.length || 0), 0);
    if (fullBranchAccess) {
      return t("addUserWizard.branchSummaryAcross", {
        branches: totalBranches,
        brands: brandCount,
      });
    }
    const sel = new Set(selectedBranchIds);
    let br = 0;
    const brandsWithSelection = new Set();
    for (const store of stores) {
      for (const b of store.branches || []) {
        if (sel.has(String(b._id))) {
          br += 1;
          brandsWithSelection.add(String(store._id));
        }
      }
    }
    return t("addUserWizard.branchSummaryAcross", {
      branches: br,
      brands: brandsWithSelection.size,
    });
  }, [stores, fullBranchAccess, selectedBranchIds, t]);

  const reviewPhoneDisplay = useMemo(() => {
    const phoneTrimmed = phone.trim();
    if (!phoneTrimmed) return "—";
    try {
      return formatKsaE164(normalizeKsaMobile(phoneTrimmed));
    } catch {
      return phoneTrimmed;
    }
  }, [phone]);

  const reviewBranchRows = useMemo(() => {
    const rid = normalizeRestaurantId(restaurantId);
    let list = [];
    if (bypass && stores.length > 0) {
      if (fullBranchAccess || selectedBranchIds.length === 0) {
        list = stores;
      } else {
        list = stores.filter((store) =>
          store.branches?.some((branch) => selectedBranchIds.includes(String(branch._id)))
        );
      }
    } else if (rid) {
      const found = stores.find((x) => String(x._id) === String(rid));
      list = found ? [found] : [{ _id: rid, name: "—", branches: [] }];
    }
    return list.map((store) => {
      const branches = store.branches || [];
      const total = branches.length;
      let selected = 0;
      if (fullBranchAccess) {
        selected = total;
      } else {
        selected = branches.filter((b) => selectedBranchIds.includes(String(b._id))).length;
      }
      return {
        storeId: String(store._id),
        storeName: store.name || "—",
        selected,
        total,
      };
    });
  }, [bypass, stores, restaurantId, fullBranchAccess, selectedBranchIds]);

  const reviewBusinessIdDisplay = useMemo(() => {
    const rid = normalizeRestaurantId(restaurantId);
    if (rid) return String(rid);
    if (bypass && stores.length === 1) return String(stores[0]._id);
    return "";
  }, [restaurantId, bypass, stores]);

  function reviewAvatarInitials() {
    const a = firstName.trim()[0] || "";
    const b = lastName.trim()[0] || "";
    if (a && b) return (a + b).toUpperCase();
    return (username || email || "?").slice(0, 2).toUpperCase();
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      backdrop="static"
      scrollable
      contentClassName="add-user-wizard-modal-content shadow-none"
    >
      <Modal.Header
        closeButton={false}
        className="rounded-0"
        style={{
          fontFamily: FONT_UI,
          paddingTop: MODAL_PAD_Y,
          paddingInline: MODAL_PAD_X,
          paddingBottom: 0,
          borderTop: "none",
          borderInline: "none",
          borderBottom: "none",
        }}
      >
        <div className="w-100">
          <div className="d-flex align-items-center justify-content-between gap-2">
            <Modal.Title
              className="mb-0 flex-grow-1 min-w-0"
              style={{ fontFamily: FONT_UI, fontWeight: 700, fontSize: 16, lineHeight: 1.5, color: WIZARD_MODAL_TITLE_COLOR }}
            >
              {t("addUserWizard.modalTitle")}
            </Modal.Title>
            <CloseButton className="flex-shrink-0 m-0" aria-label={t("close")} onClick={onHide} />
          </div>
          <div
            className="mt-1"
            key={`wizard-step-sub-${step}`}
            style={{
              color: REVIEW_SUBTITLE_COLOR,
              fontFamily: FONT_UI,
              fontWeight: 600,
              fontSize: 12,
              lineHeight: 1.5,
            }}
          >
            {t("addUserWizard.stepSubtitle", {
              current: step + 1,
              total: stepLabels.length,
              stepName: stepLabels[step] ?? "",
            })}
          </div>
          <div
            className="mt-3"
            style={{
              borderBottom: `1px solid ${WIZARD_PROGRESS_BORDER}`,
              paddingTop: 4,
              paddingBottom: 0,
              marginInline: `${-MODAL_PAD_X}px`,
              paddingInline: MODAL_PAD_X,
            }}
          >
            <StepBar current={step} labels={stepLabels} className="mb-0" />
          </div>
        </div>
      </Modal.Header>

      <Modal.Body
        className="onehub-brand-form-controls"
        style={{
          paddingTop: 10,
          paddingBottom: MODAL_PAD_Y,
          paddingInline: MODAL_PAD_X,
          fontFamily: "Inter, sans-serif",
        }}
      >
        <style>{`${ONEHUB_BRAND_FORM_CONTROLS_CSS}
.add-user-wizard-modal-content {
  border-radius: 10px;
  overflow: hidden;
  border: none;
}
.add-user-wizard-new-role-panel .form-outline .form-control,
.add-user-wizard-new-role-panel .form-control:not(.form-check-input) {
  border: 1px solid ${NEW_ROLE_INPUT_BORDER} !important;
  border-radius: 2px !important;
}
.add-user-wizard-new-role-panel .form-outline .form-control:focus,
.add-user-wizard-new-role-panel .form-control:not(.form-check-input):focus {
  border-color: ${NEW_ROLE_SWITCH_ON} !important;
  box-shadow: 0 0 0 1px rgba(8, 59, 76, 0.15) !important;
}
${ADD_USER_WIZARD_PERMISSION_ACCORDION_STYLES}
`}</style>

        {step === 0 && (
          <div>
            <p className="text-muted small mb-3">{t("rbac.enterTeamMemberProfile")}</p>
            <div className="row g-3">
              <div className="col-md-6">
                <label style={lbl}>{t("First name")} *</label>
                <MDBInput
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    setProfileErrors((er) => {
                      const next = { ...er };
                      delete next.firstName;
                      return next;
                    });
                  }}
                  onBlur={() => mergeProfileFieldErrors(["firstName"])}
                />
                {profileErrors.firstName && <small className="text-danger d-block mt-1">{profileErrors.firstName}</small>}
              </div>
              <div className="col-md-6">
                <label style={lbl}>{t("Last name")} *</label>
                <MDBInput
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.target.value);
                    setProfileErrors((er) => {
                      const next = { ...er };
                      delete next.lastName;
                      return next;
                    });
                  }}
                  onBlur={() => mergeProfileFieldErrors(["lastName"])}
                />
                {profileErrors.lastName && <small className="text-danger d-block mt-1">{profileErrors.lastName}</small>}
              </div>
              <div className="col-12">
                <label style={lbl}>{t("Username")} *</label>
                <MDBInput
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value);
                    setProfileErrors((er) => {
                      const next = { ...er };
                      delete next.username;
                      return next;
                    });
                  }}
                  onBlur={(e) => {
                    const normalized = normalizeUsernameForAccount(e.target.value);
                    setUsername(normalized);
                    void validateProfileIdentityOnBlur({ username: normalized });
                  }}
                  placeholder={t("addUserWizard.usernamePlaceholder")}
                />
                {profileErrors.username && <small className="text-danger d-block mt-1">{profileErrors.username}</small>}
              </div>
              <div className="col-12">
                <label style={lbl}>
                  {t("Email")} ({t("rbac.optionalLower")})
                </label>
                <MDBInput
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setProfileErrors((er) => {
                      const next = { ...er };
                      delete next.email;
                      return next;
                    });
                  }}
                  onBlur={(e) => void validateProfileIdentityOnBlur({ email: e.target.value })}
                  placeholder="email@example.com"
                />
                {profileErrors.email && <small className="text-danger d-block mt-1">{profileErrors.email}</small>}
              </div>
              <div className="col-12">
                <label style={lbl}>{t("addUserWizard.phoneOptional")}</label>
                <MDBInput
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value);
                    setProfileErrors((er) => {
                      const next = { ...er };
                      delete next.phone;
                      return next;
                    });
                  }}
                  onBlur={(e) => void validateProfileIdentityOnBlur({ phone: e.target.value })}
                  placeholder="+966 5XX XXX XXX"
                />
                {profileErrors.phone && <small className="text-danger d-block mt-1">{profileErrors.phone}</small>}
              </div>

              <div className="col-12">
                <div className="d-flex align-items-center gap-2">
                  <input
                    type="checkbox"
                    id="gen-pass"
                    checked={generatePassword}
                    onChange={(e) => {
                      setGeneratePassword(e.target.checked);
                      setProfileErrors((er) => {
                        const next = { ...er };
                        delete next.password;
                        delete next.confirmPassword;
                        return next;
                      });
                    }}
                    style={{
                      width: 16,
                      height: 16,
                      accentColor: BRAND,
                      cursor: "pointer",
                      margin: 0,
                    }}
                  />
                  <label
                    htmlFor="gen-pass"
                    className="small mb-0"
                    style={{
                      cursor: "pointer",
                      fontFamily: "Inter, sans-serif",
                      fontSize: 14,
                      lineHeight: 1,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {t("addUserWizard.generatePassword")}
                  </label>
                </div>
              </div>
              <div className="col-md-6">
                <label style={lbl}>{t("Password")} *</label>
                <div style={{ position: "relative" }}>
                <MDBInput
                  type={showPass ? "text" : "password"}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setProfileErrors((er) => {
                      const next = { ...er };
                      delete next.password;
                      delete next.confirmPassword;
                      return next;
                    });
                  }}
                  onBlur={() => validatePasswordFieldsOnBlur()}
                  disabled={generatePassword}
                />
                  <button
                    type="button"
                    onClick={() => setShowPass(!showPass)}
                    aria-label={showPass ? t("userManagementPage.hidePassword") : t("userManagementPage.showPassword")}
                    style={{
                      position: "absolute",
                      insetInlineEnd: 10,
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#64748b",
                    }}
                  >
                    {showPass ? <EyeOff size={18} strokeWidth={2} /> : <Eye size={18} strokeWidth={2} />}
                  </button>
                </div>
                {profileErrors.password && <small className="text-danger d-block mt-1">{profileErrors.password}</small>}
              </div>
              <div className="col-md-6">
                <label style={lbl}>{t("Confirm password")} *</label>
                <div style={{ position: "relative" }}>
                <MDBInput
                  type={showConfirmPass ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setProfileErrors((er) => {
                      const next = { ...er };
                      delete next.confirmPassword;
                      return next;
                    });
                  }}
                  onBlur={() => mergeProfileFieldErrors(["confirmPassword"])}
                  disabled={generatePassword}
                />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPass(!showConfirmPass)}
                    aria-label={showConfirmPass ? t("userManagementPage.hidePassword") : t("userManagementPage.showPassword")}
                    style={{
                      position: "absolute",
                      insetInlineEnd: 10,
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#64748b",
                    }}
                  >
                    {showConfirmPass ? <EyeOff size={18} strokeWidth={2} /> : <Eye size={18} strokeWidth={2} />}
                  </button>
                </div>
                {profileErrors.confirmPassword && (
                  <small className="text-danger d-block mt-1">{profileErrors.confirmPassword}</small>
                )}
              </div>
            </div>
          </div>
        )}

        {step === 1 && (
          <div>
            <p className="small mb-3" style={{ color: ROLE_STEP_HINT, fontFamily: FONT_UI }}>
              {t("rbac.selectRoleOrDefineNew")}
            </p>
            {roleErrors.general ? (
              <div className="small text-danger mb-3" role="alert">
                {roleErrors.general}
              </div>
            ) : null}
            <h6
              className="mb-3"
              style={{
                fontFamily: FONT_UI,
                fontStyle: "normal",
                fontWeight: ROLE_SECTION_TITLE_WEIGHT,
                fontSize: ROLE_SECTION_TITLE_FS,
                lineHeight: ROLE_SECTION_TITLE_LH,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                color: ROLE_SECTION_TITLE_COLOR,
              }}
            >
              {t("addUserWizard.selectARoleTitle")}
            </h6>
            {loadingRoles ? (
              <div className="text-center py-4">
                <MDBSpinner />
              </div>
            ) : (
              <>
                <div className="d-flex flex-column gap-2 mb-3">
                  {assignableRoles.map((r) => {
                    const rid = String(r._id);
                    const expanded = expandedRoleDetailIds.has(rid);
                    const perms = r.permissions || [];
                    const roleLabel = formatRoleDisplayName(r.name);
                    const showExpandControl = perms.length > 0;
                    const roleRowSelected = selectedRoleId === rid && !creatingRole;
                    const permChipStyle = {
                      fontSize: 10,
                      fontWeight: 400,
                      background: ROLE_CHIP_BG,
                      color: ROLE_CHIP_TEXT,
                      border: `1px solid ${ROLE_CHIP_BORDER}`,
                      padding: "0 8px",
                      borderRadius: 20,
                      lineHeight: "15px",
                      minHeight: 15,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                    };
                    return (
                      <div
                        key={r._id}
                        style={{
                          ...roleCard,
                          borderRadius: ROLE_CARD_RADIUS,
                          cursor: "default",
                          alignItems: "stretch",
                          flexDirection: "column",
                          border: `${roleRowSelected ? 2 : 1}px solid ${roleRowSelected ? NEW_ROLE_SWITCH_ON : ROLE_CARD_BORDER}`,
                          background: "#fff",
                          boxShadow: "none",
                        }}
                      >
                        <div className="d-flex align-items-start w-100" style={{ gap: 11 }}>
                          <input
                            type="radio"
                            id={`add-user-wizard-role-${rid}`}
                            name="role"
                            checked={selectedRoleId === rid && !creatingRole}
                            onChange={() => {
                              setSelectedRoleId(rid);
                              setCreatingRole(false);
                              setRoleErrors((e) => ({ ...e, general: undefined }));
                            }}
                            style={{
                              width: 16,
                              height: 16,
                              accentColor: BRAND,
                              cursor: "pointer",
                              margin: "5px 0 0 0",
                              flexShrink: 0,
                            }}
                          />
                          <label
                            htmlFor={`add-user-wizard-role-${rid}`}
                            className="flex-grow-1 min-w-0 mb-0"
                            style={{ cursor: "pointer" }}
                          >
                            <div
                              style={{
                                fontFamily: FONT_UI,
                                fontStyle: "normal",
                                fontWeight: ROLE_CARD_NAME_WEIGHT,
                                fontSize: ROLE_CARD_NAME_FS,
                                lineHeight: ROLE_CARD_NAME_LH,
                                color: ROLE_CARD_NAME_COLOR,
                                minHeight: Math.ceil(ROLE_CARD_NAME_FS * ROLE_CARD_NAME_LH),
                              }}
                            >
                              {roleLabel}
                            </div>
                            {r.description && (
                              <small
                                style={{
                                  display: "block",
                                  marginTop: 4,
                                  color: ROLE_DESC_COLOR,
                                  fontFamily: FONT_UI,
                                  fontStyle: "normal",
                                  fontWeight: ROLE_CARD_DESC_WEIGHT,
                                  fontSize: ROLE_CARD_DESC_FS,
                                  lineHeight: ROLE_CARD_DESC_LH,
                                }}
                              >
                                {r.description}
                              </small>
                            )}
                            {!expanded && perms.length > 0 && (
                              <div className="d-flex flex-wrap gap-1 mt-2">
                                {perms.slice(0, 6).map((p) => (
                                  <span key={p} style={permChipStyle}>
                                    {formatPermissionLabel(p)}
                                  </span>
                                ))}
                                {perms.length > 6 && (
                                  <span className="small" style={{ color: ROLE_STEP_HINT, fontWeight: 500 }}>
                                    +{perms.length - 6}
                                  </span>
                                )}
                              </div>
                            )}
                            {expanded && perms.length > 0 && (
                              <div className="d-flex flex-wrap gap-1 mt-2 w-100">
                                {perms.map((p) => (
                                  <span key={p} style={permChipStyle}>
                                    {formatPermissionLabel(p)}
                                  </span>
                                ))}
                              </div>
                            )}
                          </label>
                          {showExpandControl ? (
                            <button
                              type="button"
                              className="flex-shrink-0 align-self-start border-0 bg-transparent d-inline-flex align-items-center justify-content-center p-0 m-0"
                              style={{
                                color: NEW_ROLE_LABEL,
                                opacity: 0.9,
                                cursor: "pointer",
                                minWidth: 28,
                                minHeight: 28,
                                lineHeight: 0,
                              }}
                              aria-expanded={expanded}
                              aria-label={
                                expanded
                                  ? t("addUserWizard.collapseRolePermissionsAria", { role: roleLabel })
                                  : t("addUserWizard.expandRolePermissionsAria", { role: roleLabel })
                              }
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                toggleRolePermissionsExpanded(r._id);
                              }}
                            >
                              <ChevronRight
                                size={18}
                                strokeWidth={2}
                                style={{
                                  transform: (() => {
                                    const parts = [];
                                    if (expanded) parts.push("rotate(90deg)");
                                    if (isRtl) parts.push("scaleX(-1)");
                                    return parts.length ? parts.join(" ") : "none";
                                  })(),
                                  transition: "transform 0.2s ease",
                                }}
                                aria-hidden
                              />
                            </button>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div
                  className="mb-3 add-user-wizard-new-role-panel"
                  style={{
                    border: `1px solid ${NEW_ROLE_BORDER}`,
                    borderRadius: 2,
                    padding: "16px 24px",
                    background: "#fff",
                    overflow: "hidden",
                    boxShadow: "none",
                  }}
                >
                  <div className="d-flex align-items-center gap-2">
                    <label
                      className="switch-container"
                      htmlFor="new-role-switch"
                      style={{
                        cursor: canCreateRole ? "pointer" : "not-allowed",
                        display: "flex",
                        alignItems: "center",
                        opacity: canCreateRole ? 1 : 0.55,
                        pointerEvents: canCreateRole ? "auto" : "none",
                      }}
                      title={canCreateRole ? undefined : t("addUserWizard.roleCreateNoPermissionHint")}
                    >
                      <input
                        type="checkbox"
                        id="new-role-switch"
                        checked={creatingRole && canCreateRole}
                        disabled={!canCreateRole}
                        aria-disabled={!canCreateRole}
                        onChange={(e) => {
                          if (!canCreateRole) return;
                          setCreatingRole(e.target.checked);
                          if (e.target.checked) setSelectedRoleId("");
                          setRoleErrors({});
                        }}
                        style={{ display: "none" }}
                      />
                      <span
                        className="switch-slider"
                        style={{
                          position: "relative",
                          display: "inline-block",
                          width: NEW_ROLE_SWITCH_W,
                          height: NEW_ROLE_SWITCH_H,
                          backgroundColor:
                            creatingRole && canCreateRole ? NEW_ROLE_SWITCH_ON : NEW_ROLE_SWITCH_OFF,
                          borderRadius: 24,
                          transition: "background-color 0.2s ease",
                          cursor: canCreateRole ? "pointer" : "not-allowed",
                        }}
                      >
                        <span
                          className="switch-thumb"
                          style={{
                            position: "absolute",
                            top: (NEW_ROLE_SWITCH_H - NEW_ROLE_SWITCH_THUMB) / 2,
                            insetInlineStart: newRoleSwitchThumbInset(creatingRole && canCreateRole),
                            width: NEW_ROLE_SWITCH_THUMB,
                            height: NEW_ROLE_SWITCH_THUMB,
                            backgroundColor: "#fff",
                            borderRadius: "50%",
                            transition: "inset-inline-start 0.2s ease",
                            boxShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                          }}
                        />
                      </span>
                    </label>
                    <label
                      htmlFor="new-role-switch"
                      className="fw-semibold mb-0"
                      style={{
                        cursor: canCreateRole ? "pointer" : "not-allowed",
                        fontFamily: FONT_UI,
                        fontStyle: "normal",
                        fontWeight: NEW_ROLE_HEADING_WEIGHT,
                        fontSize: NEW_ROLE_HEADING_FS,
                        lineHeight: NEW_ROLE_HEADING_LH,
                        display: "flex",
                        alignItems: "center",
                        minHeight: Math.ceil(NEW_ROLE_HEADING_FS * NEW_ROLE_HEADING_LH),
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        color: NEW_ROLE_LABEL,
                        opacity: canCreateRole ? 1 : 0.55,
                        pointerEvents: canCreateRole ? "auto" : "none",
                      }}
                      title={canCreateRole ? undefined : t("addUserWizard.roleCreateNoPermissionHint")}
                    >
                      {t("rbac.addNewRoleToggle")}
                    </label>
                  </div>
                  {!canCreateRole ? (
                    <p className="small mb-0 mt-2" role="note" style={{ color: ROLE_STEP_HINT, fontFamily: FONT_UI }}>
                      {t("addUserWizard.roleCreateNoPermissionHint")}
                    </p>
                  ) : null}

                  {creatingRole && canCreateRole && (
                    <div
                      className="mt-3 pt-3"
                      style={{
                        borderTop: `1px solid ${NEW_ROLE_BORDER}`,
                        marginInline: -24,
                        marginBottom: -16,
                        paddingInline: 24,
                        paddingBottom: 8,
                        paddingTop: 8,
                        background: NEW_ROLE_INNER_BG,
                        borderBottomLeftRadius: 2,
                        borderBottomRightRadius: 2,
                      }}
                    >
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label style={newRoleFieldLbl}>{t("rbac.roleNameLabel")} *</label>
                          <MDBInput
                            value={newRoleName}
                            onChange={(e) => {
                              setNewRoleName(e.target.value);
                              setRoleErrors((er) => ({ ...er, newRoleName: undefined }));
                            }}
                            onBlur={() => validateNewRoleNameOnBlur()}
                            placeholder={t("rbac.roleNamePlaceholderStaff")}
                          />
                          {roleErrors.newRoleName ? (
                            <small className="text-danger d-block mt-1">{roleErrors.newRoleName}</small>
                          ) : null}
                        </div>
                        <div className="col-md-6">
                          <label style={newRoleFieldLbl}>{t("Description")} ({t("rbac.optionalLower")})</label>
                          <MDBInput value={newRoleDesc} onChange={(e) => setNewRoleDesc(e.target.value)} />
                        </div>
                        <div className="col-12">
                          <div className="add-user-wizard-permissions-block">
                            <PermissionAccordion
                              domains={permDomains.length ? permDomains : null}
                              registry={permRegistry}
                              selected={selectedPerms}
                              embeddedInForm
                              onChangeSelected={(rawNext) => {
                                const { permissions: fixed, toastKey } = reconcileUsersMutateRequiresRolesView(
                                  selectedPerms,
                                  rawNext
                                );
                                const list = [...new Set(fixed)];
                                setSelectedPerms(list);
                                if (toastKey === "autoAddedDeps") {
                                  toast.info(t("rbac.usersMgmtAutoAddedRolesViewAndCreate"));
                                  const sectionId =
                                    permissionDomainIdContainingKey(permDomains, ROLES_VIEW_PERM) || "roles";
                                  setExpandedPermMods((prev) => new Set(prev).add(sectionId));
                                } else if (toastKey === "stripped") {
                                  toast.info(t("rbac.usersMgmtStrippedNeedsRolesView"));
                                }
                                setRoleErrors((er) => ({
                                  ...er,
                                  permissions:
                                    creatingRole && canCreateRole && list.length === 0
                                      ? t("rbac.permissionRequiredForCreate")
                                      : undefined,
                                }));
                              }}
                              expanded={expandedPermMods}
                              onToggleExpand={togglePermExpand}
                              dir={i18n.dir()}
                              t={t}
                            />
                          </div>
                          {roleErrors.permissions ? (
                            <p
                              className="text-danger mt-1 mb-0"
                              style={{ fontSize: "0.8125rem", lineHeight: 1.35 }}
                            >
                              {roleErrors.permissions}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {step === 2 && (
          <div>
            <h6
              className="mb-3"
              style={{
                fontFamily: FONT_UI,
                fontWeight: 700,
                fontSize: "1rem",
                lineHeight: 1.5,
                color: BRANCH_SECTION_TITLE_COLOR,
              }}
            >
              {t("addUserWizard.selectBrandBranchTitle")}
            </h6>

            {scopeErrors.general ? (
              <div className="small text-danger mb-3" role="alert">
                {scopeErrors.general}
              </div>
            ) : null}

            <div
              className="d-flex flex-wrap align-items-center justify-content-between gap-2 px-3 py-2 mb-3"
              style={{
                background: BRANCH_SELECT_ALL_BG,
                border: `1px solid ${BRANCH_SELECT_ALL_BORDER}`,
                borderRadius: BRANCH_CARD_RADIUS,
                fontFamily: FONT_UI,
              }}
            >
              <div className="d-flex align-items-center gap-2 mb-0">
                <input
                  type="checkbox"
                  id="select-all-brands-branches"
                  checked={fullBranchAccess}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setFullBranchAccess(checked);
                    if (checked) setSelectedBranchIds([]);
                    setScopeErrors({});
                  }}
                  style={{
                    width: 16,
                    height: 16,
                    accentColor: BRAND,
                    cursor: "pointer",
                    margin: 0,
                  }}
                />
                <label
                  htmlFor="select-all-brands-branches"
                  className="fw-semibold mb-0"
                  style={{
                    cursor: "pointer",
                    fontFamily: FONT_UI,
                    fontSize: 14,
                    fontWeight: 600,
                    lineHeight: 1.45,
                    display: "flex",
                    alignItems: "center",
                    color: BRANCH_SECTION_TITLE_COLOR,
                  }}
                >
                  {t("addUserWizard.selectAllBrandsBranches")}
                </label>
              </div>
              <span className="text-nowrap" style={{ fontSize: 13, color: BRANCH_SUMMARY_TEXT, fontFamily: FONT_UI }}>
                {branchStepSummary}
              </span>
            </div>

            {loadingStores ? (
              <div className="text-center py-5">
                <MDBSpinner />
              </div>
            ) : fullBranchAccess ? (
              <p className="small mb-0 px-1" style={{ fontFamily: FONT_UI, color: BRANCH_SUMMARY_TEXT }}>
                {t("addUserWizard.fullAccessBranchesHint")}
              </p>
            ) : (
              <div className="mb-4">
                {stores.map((store) => {
                  const branches = store.branches || [];
                  const expanded = expandedStores.has(store._id);
                  const bids = branches.map((b) => String(b._id));
                  const numSel = bids.filter((id) => selectedBranchIds.includes(id)).length;
                  const allSelected = branches.length > 0 && numSel === branches.length;
                  const someSelected = numSel > 0 && numSel < branches.length;

                  return (
                    <div
                      key={store._id}
                      className="mb-2"
                      style={{
                        border: `1px solid ${BRANCH_CARD_BORDER}`,
                        borderRadius: BRANCH_CARD_RADIUS,
                        background: "#fff",
                        overflow: "hidden",
                        fontFamily: FONT_UI,
                      }}
                    >
                      <div
                        className="d-flex align-items-center gap-2 px-3 py-2"
                        role={branches.length > 0 ? "button" : undefined}
                        tabIndex={branches.length > 0 ? 0 : undefined}
                        aria-expanded={branches.length > 0 ? expanded : undefined}
                        aria-label={
                          branches.length > 0
                            ? `${store.name || ""}: ${
                                expanded
                                  ? t("addUserWizard.collapseBranchList")
                                  : t("addUserWizard.expandBranchList")
                              }`
                            : undefined
                        }
                        onClick={(e) => {
                          if (!branches.length) return;
                          const el = e.target;
                          if (typeof el.closest === "function" && el.closest(".form-check")) return;
                          toggleStoreExpandedRow(store._id);
                        }}
                        onKeyDown={(e) => {
                          if (!branches.length) return;
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            toggleStoreExpandedRow(store._id);
                          }
                        }}
                        style={{
                          cursor: branches.length > 0 ? "pointer" : undefined,
                          fontFamily: FONT_UI,
                        }}
                      >
                        <span
                          className="d-flex align-items-center justify-content-center flex-shrink-0"
                          style={{ width: 16, flexBasis: 16 }}
                        >
                          <input
                            type="checkbox"
                            id={`store-all-${store._id}`}
                            className="m-0 flex-shrink-0"
                            checked={branches.length > 0 && allSelected}
                            ref={(el) => {
                              if (el && branches.length) el.indeterminate = someSelected;
                            }}
                            onChange={() => toggleAllBranchesInStore(store)}
                            disabled={branches.length === 0}
                            onClick={(e) => e.stopPropagation()}
                            aria-label={`${t("rbac.selectAllBranches")}: ${store.name || ""}`}
                            style={{
                              width: 16,
                              height: 16,
                              accentColor: BRAND,
                              cursor: branches.length === 0 ? "not-allowed" : "pointer",
                              margin: 0,
                              opacity: branches.length === 0 ? 0.6 : 1,
                            }}
                          />
                        </span>
                        <div className="flex-grow-1 min-w-0">
                          <div className="fw-bold" style={{ fontSize: 14, color: BRANCH_SECTION_TITLE_COLOR }}>
                            {store.name}
                          </div>
                          <div style={{ fontSize: 12, marginTop: 2, lineHeight: 1.35, color: BRANCH_SUMMARY_TEXT }}>
                            {t("addUserWizard.branchCountInline", { count: branches.length })}
                          </div>
                        </div>
                        {branches.length > 0 ? (
                          <span
                            className="flex-shrink-0 align-self-center d-inline-flex align-items-center"
                            style={{ lineHeight: 0, color: BRANCH_SECTION_TITLE_COLOR, opacity: 0.75 }}
                            aria-hidden
                          >
                            <ChevronRight
                              size={18}
                              strokeWidth={2}
                              style={{
                                transform: (() => {
                                  const parts = [];
                                  if (expanded) parts.push("rotate(90deg)");
                                  if (isRtl) parts.push("scaleX(-1)");
                                  return parts.length ? parts.join(" ") : "none";
                                })(),
                                transition: "transform 0.2s ease",
                              }}
                            />
                          </span>
                        ) : null}
                      </div>
                      {expanded && branches.length > 0 ? (
                        <div
                          className="px-3"
                          style={{
                            background: BRANCH_EXPANDED_BG,
                            borderTop: `1px solid ${BRANCH_EXPANDED_BORDER_TOP}`,
                            paddingTop: 10,
                            paddingBottom: 10,
                          }}
                        >
                          {branches.map((b, idx) => (
                            <div
                              key={b._id}
                              className="d-flex align-items-center gap-2"
                              style={{
                                paddingInlineStart: BRANCH_CHECKBOX_ALIGN_INSET,
                                paddingTop: idx === 0 ? 0 : 6,
                                paddingBottom: idx === branches.length - 1 ? 0 : 6,
                              }}
                            >
                              <span
                                className="d-flex align-items-center justify-content-center flex-shrink-0"
                                style={{ width: 16, flexBasis: 16 }}
                              >
                                <input
                                  type="checkbox"
                                  id={`branch-${b._id}`}
                                  checked={selectedBranchIds.includes(String(b._id))}
                                  onChange={() => toggleBranch(b._id)}
                                  style={{
                                    width: 16,
                                    height: 16,
                                    accentColor: BRAND,
                                    cursor: "pointer",
                                    margin: 0,
                                  }}
                                />
                              </span>
                              <label
                                htmlFor={`branch-${b._id}`}
                                className="mb-0 flex-grow-1 min-w-0"
                                style={{
                                  cursor: "pointer",
                                  fontFamily: FONT_UI,
                                  fontSize: 13,
                                  lineHeight: 1.35,
                                  display: "flex",
                                  alignItems: "center",
                                  color: BRANCH_SECTION_TITLE_COLOR,
                                }}
                              >
                                {b.name || String(b._id)}
                              </label>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {step === 3 && (
          <div
            className="bg-white"
            style={{
              border: `1px solid ${REVIEW_CARD_BORDER}`,
              borderRadius: REVIEW_USER_CARD_RADIUS,
              padding: REVIEW_USER_CARD_PAD,
              display: "flex",
              flexDirection: "column",
              gap: REVIEW_USER_CARD_GAP,
              fontFamily: FONT_UI,
              boxSizing: "border-box",
            }}
          >
            <div
              className="d-flex align-items-center"
              style={{
                gap: 16,
                paddingBottom: 10,
                borderBottom: `1px solid ${REVIEW_FIGMA_ROW_BORDER}`,
              }}
            >
              <div
                className="d-flex align-items-center justify-content-center flex-shrink-0 fw-bold"
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 20,
                  backgroundColor: REVIEW_AVATAR_BG_FIGMA,
                  color: REVIEW_AVATAR_FG_FIGMA,
                  fontSize: 16,
                  lineHeight: 1.5,
                }}
              >
                {reviewAvatarInitials()}
              </div>
              <div className="min-w-0 flex-grow-1">
                <div style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.5, color: REVIEW_FIGMA_TEXT }}>{displayName}</div>
                <div style={{ fontSize: 12, fontWeight: 400, lineHeight: 1.5, color: REVIEW_FIGMA_TEXT, marginTop: 2 }}>
                  {roleName}
                </div>
              </div>
            </div>

            <div className="d-flex flex-column" style={{ gap: 1 }}>
              <ReviewFigmaRow
                label={t("addUserWizard.reviewBusinessId")}
                value={reviewBusinessIdDisplay || "—"}
              />
              <ReviewFigmaRow label={t("Username")} value={normalizeUsernameForAccount(username) || "—"} />
              <ReviewFigmaRow
                label={t("Password")}
                value={password.trim() ? password : "—"}
                monospace
              />
              <ReviewFigmaRow label={t("Email")} value={email.trim() || "—"} />
              <ReviewFigmaRow label={t("Phone")} value={reviewPhoneDisplay} />
            </div>

            <div
              className="d-flex justify-content-between align-items-start"
              style={{
                padding: "8px 0",
                borderBottom: `1px solid ${REVIEW_FIGMA_ROW_BORDER}`,
                gap: 12,
              }}
            >
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 400,
                  lineHeight: 1.5,
                  color: REVIEW_FIGMA_TEXT,
                  flexShrink: 0,
                  maxWidth: "42%",
                }}
              >
                {t("addUserWizard.brandsBranchesAccess")}
              </span>
              <div className="d-flex flex-column align-items-end" style={{ gap: 6, minWidth: 0 }}>
                {reviewBranchRows.length === 0 ? (
                  <span style={{ fontSize: 12, fontWeight: 600, color: REVIEW_FIGMA_TEXT }}>—</span>
                ) : (
                  reviewBranchRows.map((row) => (
                    <div
                      key={row.storeId}
                      className="d-flex align-items-center flex-wrap"
                      style={{ gap: 4, justifyContent: "flex-end" }}
                    >
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          lineHeight: 1.5,
                          color: REVIEW_FIGMA_TEXT,
                          textAlign: "end",
                          wordBreak: "break-word",
                        }}
                      >
                        {row.storeName}
                      </span>
                      <span
                        className="rounded-pill flex-shrink-0"
                        style={{
                          background: REVIEW_BRANCH_PILL_BG_FIGMA,
                          border: `1px solid ${REVIEW_BRANCH_PILL_BORDER_FIGMA}`,
                          color: REVIEW_BRANCH_PILL_TEXT_FIGMA,
                          padding: "0 8px",
                          fontSize: 12,
                          fontWeight: 500,
                          lineHeight: "18px",
                          minHeight: 18,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {t("addUserWizard.branchPill", { selected: row.selected, total: row.total })}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </Modal.Body>

        <Modal.Footer
        className="d-flex justify-content-between align-items-center w-100 flex-wrap gap-3 rounded-0"
        dir={isRtl ? "rtl" : "ltr"}
        style={{
          fontFamily: FONT_UI,
          padding: "20px 24px",
          borderInline: "none",
          borderBottom: "none",
          borderTop: `1px solid ${WIZARD_PROGRESS_BORDER}`,
        }}
      >
        <div className="d-flex align-items-center flex-shrink-0 min-w-0">
          {step > 0 ? (
            <Button
              outline
              className="m-0"
              onClick={back}
              disabled={submitting}
              style={{
                borderColor: WIZARD_PRIMARY_HEX,
                color: WIZARD_PRIMARY_HEX,
                borderRadius: 4,
                fontSize: 12,
                fontWeight: 600,
                padding: "6px 12px 6px 6px",
                lineHeight: 1.5,
              }}
            >
              {isRtl ? (
                <>
                  {t("rbac.back")}{" "}
                  <ChevronLeft size={16} className="d-inline-block align-text-bottom ms-1" aria-hidden />
                </>
              ) : (
                <>
                  <ChevronLeft size={16} className="d-inline-block align-text-bottom me-1" aria-hidden />
                  {t("rbac.back")}
                </>
              )}
            </Button>
          ) : null}
        </div>
        <div className="d-flex align-items-center flex-shrink-0" style={{ marginInlineStart: "auto" }}>
          {step < stepLabels.length - 1 ? (
            <Button
              className="m-0"
              onClick={() => void goForward()}
              disabled={submitting || checkingIdentity}
              style={{
                background: WIZARD_PRIMARY_HEX,
                border: "none",
                borderRadius: 4,
                fontSize: 12,
                fontWeight: 700,
                padding: "6px 12px",
                lineHeight: 1.5,
              }}
            >
              {checkingIdentity ? (
                <MDBSpinner size="sm" color="light" />
              ) : isRtl ? (
                <>
                  {t("rbac.continue")}{" "}
                  <ChevronLeft size={16} className="d-inline-block align-text-bottom ms-1" aria-hidden />
                </>
              ) : (
                <>
                  {t("rbac.continue")}
                  <ChevronRight size={16} className="d-inline-block align-text-bottom ms-1" aria-hidden />
                </>
              )}
            </Button>
          ) : (
            <Button
              className="m-0"
              onClick={handleSubmit}
              disabled={submitting || checkingIdentity}
              style={{
                background: WIZARD_PRIMARY_HEX,
                border: "none",
                borderRadius: 4,
                fontSize: 12,
                fontWeight: 700,
                minWidth: 53,
                padding: "6px 12px",
                lineHeight: 1.5,
              }}
            >
              {submitting || checkingIdentity ? <MDBSpinner size="sm" color="light" /> : t("addUserWizard.sendInvite")}
            </Button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
}

const lbl = { fontSize: 12, fontWeight: 600, color: "#212529", marginBottom: 4, display: "block" };
/** Labels inside Add new role builder (Figma — Role name / Description). */
const newRoleFieldLbl = {
  fontSize: 10,
  fontWeight: 600,
  color: NEW_ROLE_LABEL,
  marginBottom: 2,
  display: "block",
  fontFamily: FONT_UI,
  lineHeight: 1.5,
};
const roleCard = {
  display: "flex",
  alignItems: "flex-start",
  gap: 0,
  padding: "10px 8px",
  cursor: "pointer",
  transition: "border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease",
};
